import {div, filler, hbox} from '@xh/hoist/cmp/layout';
import {hoistCmp, HoistProps, uses} from '@xh/hoist/core';
import {button} from '@xh/hoist/desktop/cmp/button';
import {buttonGroupInput, numberInput, select, textInput} from '@xh/hoist/desktop/cmp/input';
import {Icon, IconFactory} from '@xh/hoist/icon';
import {startCase} from 'lodash';
import {dragHandle, draggableList} from '../../draggableList/DraggableList';
import {configPanelItem, configPanelSwitch, specJsonSection, stackedPanel} from '../../gridSpec/configurator/Common';
import {specAssistant} from '../../spec/assistant/SpecAssistant';
import {SERIES_COLOR_VALUES} from '../ChartSpecBuilder';
import {
    AxisId,
    CHART_TYPES,
    ChartSpecSeries,
    ChartType,
    LEGEND_POSITIONS,
    MAX_INNER_SIZE,
    OHLC_KEYS,
    SERIES_COLORS,
    SERIES_TYPES,
    STACKINGS
} from '../ChartSpecTypes';
import {ChartSpecConfiguratorModel, DATE_FORMAT_PRESETS, NUMBER_FORMAT_PRESETS} from './ChartSpecConfiguratorModel';
import './ChartSpecConfigurator.scss';
import {chartBarHorizontalIcon, chartCandlestickIcon, chartScatterIcon, waveIcon, waveSineIcon} from '../../../../core/Icons';

const options = (values: readonly string[]) => values.map(value => ({value, label: startCase(value)}));

/** Light icons, registered in Bootstrap.ts. */
const TYPE_ICONS: Record<ChartType, IconFactory> = {
    line: Icon.chartLine,
    spline: waveSineIcon,
    area: Icon.chartArea,
    areaspline: waveIcon,
    column: Icon.chartBar,
    bar: chartBarHorizontalIcon,
    scatter: chartScatterIcon,
    pie: Icon.chartPie,
    candle: chartCandlestickIcon
};

const CANDLE_STYLE_OPTIONS = [
        {value: 'candlestick', label: 'Candle'},
        {value: 'hollowcandlestick', label: 'Hollow Candle'},
        {value: 'heikinashi', label: 'Heikin Ashi'},
        {value: 'ohlc', label: 'OHLC'},
        {value: 'hlc', label: 'HLC'}
    ],
    SERIES_TYPE_OPTIONS = [{value: null, label: 'Chart type'}, ...options(SERIES_TYPES)],
    LEGEND_OPTIONS = options(LEGEND_POSITIONS),
    STACKING_OPTIONS = options(STACKINGS),
    AXIS_OPTIONS = [
        {value: 'primary', label: 'Left axis'},
        {value: 'secondary', label: 'Right axis'}
    ],
    COLOR_OPTIONS = [{value: null, label: 'Auto'}, ...options(SERIES_COLORS)],
    DATA_LABEL_OPTIONS = [
        {value: 'none', label: 'None'},
        {value: 'name', label: 'Name'},
        {value: 'value', label: 'Value'},
        {value: 'nameAndValue', label: 'Name + Value'}
    ];

/**
 * Configurator for a ChartSpec - every control dispatches ops to the ChartSpecModel, the same path
 * the AI assistant (the first section) uses, so both always show and edit the same spec.
 */
export const chartSpecConfigurator = hoistCmp.factory<ChartSpecConfiguratorModel>({
    model: uses(ChartSpecConfiguratorModel),
    render({model}) {
        return div({
            className: 'gs-configurator cs-configurator',
            items: [specAssistant({model: model.assistantModel}), chartSection(), seriesSection(), axesSection(), specJsonSection({specModel: model.specModel})]
        });
    }
});

//------------------------
// Chart
//------------------------
const chartSection = hoistCmp.factory<ChartSpecConfiguratorModel>(({model}) => {
    const {spec, isSeriesLayout, layoutOptions} = model,
        isPie = spec.type === 'pie',
        isCandle = spec.type === 'candle',
        canStack = ['column', 'bar', 'area', 'areaspline'].includes(spec.type);

    return stackedPanel({
        title: 'Chart',
        items: [
            configPanelItem({
                item: buttonGroupInput({
                    className: 'cs-configurator__types',
                    value: spec.type,
                    onChange: v => model.setOption('type', v),
                    minimal: true,
                    outlined: true,
                    items: CHART_TYPES.map(type =>
                        button({
                            value: type,
                            icon: TYPE_ICONS[type]({prefix: 'fal'}),
                            title: startCase(type),
                            // Candles are drawn one per row
                            disabled: type === 'candle' && !isSeriesLayout
                        })
                    )
                })
            }),
            configPanelItem({
                label: 'Candle Style',
                omit: !isCandle,
                item: select({
                    value: spec.candleStyle,
                    onChange: v => model.setOption('candleStyle', v),
                    options: CANDLE_STYLE_OPTIONS,
                    enableFilter: false,
                    width: 150
                })
            }),
            div({
                className: 'cs-configurator__ohlc',
                omit: !isCandle || !spec.ohlc,
                items: OHLC_KEYS.map(key =>
                    configPanelItem({
                        key,
                        label: startCase(key),
                        omit: key === 'open' && spec.candleStyle === 'hlc',
                        item: select({value: spec.ohlc?.[key], onChange: v => model.setOhlcField(key, v), options: model.numberFieldOptions, width: 150})
                    })
                )
            }),
            configPanelItem({
                label: 'Layout',
                omit: layoutOptions.length < 2,
                item: select({value: spec.layout, onChange: v => model.setOption('layout', v), options: layoutOptions, enableFilter: false, width: 150})
            }),
            configPanelItem({
                label: isPie ? 'Slices' : 'X Axis',
                omit: !isSeriesLayout,
                item: select({value: spec.xField, onChange: v => model.setOption('xField', v), options: model.xFieldOptions, width: 150})
            }),
            configPanelItem({
                label: 'Legend',
                item: select({value: spec.legend, onChange: v => model.setOption('legend', v), options: LEGEND_OPTIONS, enableFilter: false, width: 150})
            }),
            configPanelItem({
                label: 'Stacking',
                omit: !canStack,
                item: select({value: spec.stacking, onChange: v => model.setOption('stacking', v), options: STACKING_OPTIONS, enableFilter: false, width: 150})
            }),
            configPanelItem({
                label: 'Data Labels',
                item: select({
                    value: spec.dataLabels,
                    onChange: v => model.setOption('dataLabels', v),
                    options: DATA_LABEL_OPTIONS,
                    enableFilter: false,
                    width: 150
                })
            }),
            configPanelItem({
                omit: isPie || !isSeriesLayout,
                item: configPanelSwitch({label: 'Shared Tooltip', value: spec.sharedTooltip, onChange: v => model.setOption('sharedTooltip', v)})
            }),
            configPanelItem({
                omit: isPie,
                item: configPanelSwitch({label: 'Point Markers', value: spec.markers, onChange: v => model.setOption('markers', v)})
            }),
            configPanelItem({
                label: 'Ring Hole (%)',
                omit: !isPie,
                item: numberInput({
                    value: spec.innerSize,
                    onChange: v => model.setOption('innerSize', Math.min(Math.max(v ?? 0, 0), MAX_INNER_SIZE)),
                    min: 0,
                    max: MAX_INNER_SIZE,
                    stepSize: 5,
                    placeholder: '0 = full pie',
                    width: 150
                })
            })
        ]
    });
});

//------------------------
// Series
//------------------------
const seriesSection = hoistCmp.factory<ChartSpecConfiguratorModel>(({model}) => {
    const {spec} = model;

    return stackedPanel({
        title: model.isSeriesLayout ? 'Series' : 'Categories',
        items: [
            configPanelItem({
                label: 'Add',
                item: select({
                    value: null,
                    onChange: field => model.addSeries(field),
                    options: model.addableFieldOptions,
                    placeholder: 'Add or show a field...',
                    flex: 1,
                    width: null
                })
            }),
            draggableList({
                data: spec.series,
                idSpec: 'field',
                onDrop: ({dragTargetIdx, dropTargetIdx}) => model.moveSeries(spec.series[dragTargetIdx].field, dropTargetIdx),
                renderer: ({data, index}) => seriesItem({series: data as ChartSpecSeries, index})
            })
        ]
    });
});

interface SeriesItemProps extends HoistProps<ChartSpecConfiguratorModel> {
    series: ChartSpecSeries;
    index: number;
}

const seriesItem = hoistCmp.factory<SeriesItemProps>(({model, series, index}) => {
    const {field, hidden} = series,
        {specModel, isSeriesLayout} = model,
        swatch = series.color ? SERIES_COLOR_VALUES[series.color] : null;

    return div({
        className: 'cs-configurator__series',
        items: [
            configPanelItem({
                omitFiller: true,
                items: [
                    div({className: 'cs-configurator__swatch', style: swatch ? {background: swatch} : null}),
                    textInput({
                        value: series.name ?? '',
                        placeholder: specModel.fieldLabel(field),
                        onChange: v => model.updateSeries(field, {name: v || null}, true),
                        commitOnChange: true,
                        flex: 1,
                        width: null
                    }),
                    button({
                        icon: hidden ? Icon.eyeSlash({prefix: 'fal'}) : Icon.eye({intent: 'primary'}),
                        minimal: true,
                        title: hidden ? 'Show' : 'Hide',
                        onClick: () => model.toggleSeriesHidden(field)
                    }),
                    button({icon: Icon.x(), intent: 'danger', minimal: true, title: 'Remove', onClick: () => model.removeSeries(field)}),
                    dragHandle({index})
                ]
            }),
            hbox({
                className: 'cs-configurator__series-options',
                items: [
                    select({
                        value: series.color ?? null,
                        onChange: color => model.updateSeries(field, {color}),
                        options: COLOR_OPTIONS,
                        enableFilter: false,
                        flex: 1,
                        width: null
                    }),
                    select({
                        omit: !isSeriesLayout || ['pie', 'bar'].includes(model.spec.type),
                        value: series.type ?? null,
                        onChange: type => model.updateSeries(field, {type}),
                        options: SERIES_TYPE_OPTIONS,
                        enableFilter: false,
                        flex: 1,
                        width: null
                    }),
                    select({
                        omit: !isSeriesLayout || model.spec.type === 'pie',
                        value: series.axis ?? 'primary',
                        onChange: axis => model.updateSeries(field, {axis}),
                        options: AXIS_OPTIONS,
                        enableFilter: false,
                        flex: 1,
                        width: null
                    })
                ]
            })
        ]
    });
});

//------------------------
// Axes
//------------------------
const axesSection = hoistCmp.factory<ChartSpecConfiguratorModel>(({model}) => {
    const {spec, isSeriesLayout} = model;
    if (spec.type === 'pie') return null;

    return stackedPanel({
        title: 'Axes',
        items: [
            axisEditor({axis: 'x', label: 'X Axis', omit: !isSeriesLayout}),
            axisEditor({axis: 'primary', label: isSeriesLayout ? 'Left Axis' : 'Value Axis'}),
            axisEditor({axis: 'secondary', label: 'Right Axis', omit: !model.showsSecondaryAxis})
        ]
    });
});

interface AxisEditorProps extends HoistProps<ChartSpecConfiguratorModel> {
    axis: AxisId;
    label: string;
}

const axisEditor = hoistCmp.factory<AxisEditorProps>(({model, axis, label}) => {
    const settings = model.spec.axes[axis],
        isX = axis === 'x',
        // The x axis takes a date format for dates; categories are shown as they are
        presets = isX ? (model.isXDate ? DATE_FORMAT_PRESETS : null) : NUMBER_FORMAT_PRESETS;

    return div({
        className: 'cs-configurator__axis',
        items: [
            configPanelItem({
                className: 'config-panel-item config-panel-item_header',
                label,
                item: textInput({
                    value: settings.title ?? '',
                    placeholder: 'Title',
                    onChange: v => model.updateAxis(axis, {title: v || null}, true),
                    commitOnChange: true,
                    width: 170
                })
            }),
            configPanelItem({
                label: 'Format',
                omit: !presets,
                item: select({
                    value: model.axisPresetValue(axis),
                    onChange: v =>
                        model.setAxisFormat(
                            axis,
                            presets.find(p => p.value === v)
                        ),
                    options: [{value: null, label: 'Default'}, ...(presets ?? [])],
                    enableFilter: false,
                    width: 170
                })
            }),
            configPanelItem({
                label: 'Range',
                omit: isX,
                omitFiller: true,
                items: [
                    filler(),
                    numberInput({
                        value: settings.min ?? null,
                        placeholder: 'Min',
                        onChange: v => model.updateAxis(axis, {min: v ?? null}),
                        width: 80
                    }),
                    numberInput({
                        value: settings.max ?? null,
                        placeholder: 'Max',
                        onChange: v => model.updateAxis(axis, {max: v ?? null}),
                        width: 80
                    })
                ]
            })
        ]
    });
});
